// The identity page's *Voices and recordings* tab (ADR-0062, ADR-0068, ADR-0077): the person's
// voiceprints in the Voices card — the voice sample first, then the recording voices confirmed
// to them — and under it the recordings they speak in, as whole files, with the detections
// waiting for review. One audited audio read serves the recordings and the samples on their
// way; the Voices card has a read of its own.
import { Mic, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PendingSampleRow } from '@/components/identities/voices/IdentityVoicesCard'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { toVoiceSampleQuality } from '@/features/humint/mappers/subject-page'
import { IdentityVoices } from '@/features/identities/voices/IdentityVoices'
import { VoiceSampleHost } from '@/pages/humint/voice/VoiceSampleHost'
import type { IdentityAudioRecord as AudioRecord } from '@/services/api/identity-audio'
import { IdentityAudioRecord } from './IdentityAudioRecord'
import { RecordingMatchDialog } from './RecordingMatchDialog'
import { useIdentityAudio } from './useIdentityAudio'

const COPY = 'identity.detail.audio'

/** The samples the Voices card cannot know of yet: still being checked, or left without a voiceprint. */
export function pendingSamples(items: readonly AudioRecord[]): PendingSampleRow[] {
  return items
    .filter(row => row.kind === 'voice_sample' && row.state !== 'done' && row.capture_id)
    .map(row => ({
      capture_id: row.capture_id ?? '',
      recorded_at: row.recorded_at,
      state: row.state,
    }))
}

/** The recordings the person speaks in, each a whole file with its detections. */
export function IdentityAudioList({
  items,
  caseId,
  onCompare,
}: {
  items: AudioRecord[]
  caseId: string
  onCompare?: (matchId: string) => void
}) {
  const { t } = useTranslation()
  const rows = items.filter(row => row.kind === 'recording')
  return (
    <section className="space-y-3" aria-label={t('identity.voices.recordings.title')}>
      <div>
        <h2 className="text-base font-semibold">
          {t('identity.voices.recordings.title')}{' '}
          <span className="ml-2 text-sm font-normal text-muted-foreground">{rows.length}</span>
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('identity.voices.recordings.hint')}</p>
      </div>
      <Card>
        <CardContent className="divide-y divide-border p-5">
          {rows.length ? (
            rows.map(row => (
              <IdentityAudioRecord
                key={row.document_id}
                row={row}
                caseId={caseId}
                onCompare={onCompare}
              />
            ))
          ) : (
            <p className="py-3 text-sm text-muted-foreground">{t(`${COPY}.empty.recording`)}</p>
          )}
        </CardContent>
      </Card>
    </section>
  )
}

function AudioHeader({ actions }: { actions: { refresh: () => void; add?: () => void } }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="max-w-2xl space-y-1">
        <h2 className="text-lg font-semibold">
          {t('identity.detail.sections.audioRecordsAndVoice')}
        </h2>
        <p className="text-sm text-muted-foreground">{t(`${COPY}.hint`)}</p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-label={t(`${COPY}.refresh`)}
          onClick={actions.refresh}
        >
          <RefreshCw className="size-4" />
        </Button>
        {actions.add && (
          <Button onClick={actions.add}>
            <Mic className="size-4" />
            {t(`${COPY}.addSample`)}
          </Button>
        )}
      </div>
    </div>
  )
}

export function IdentityAudio({ caseId, identityId }: { caseId: string; identityId: string }) {
  const { t } = useTranslation()
  const audio = useIdentityAudio(caseId, identityId)
  const { query, retry } = audio
  if (query.isPending) return <Skeleton className="h-48 w-full" />
  if (query.isError || !query.data)
    return (
      <div role="alert" className="space-y-3">
        <p>{t(`${COPY}.loadFailed`)}</p>
        <Button variant="outline" onClick={retry}>
          {t('humint.common.actions.retry')}
        </Button>
      </div>
    )
  return <IdentityAudioContent context={{ caseId, identityId }} audio={audio} />
}

function IdentityAudioContent({
  context,
  audio,
}: {
  context: { caseId: string; identityId: string }
  audio: ReturnType<typeof useIdentityAudio>
}) {
  const [open, setOpen] = useState(false)
  const [matchId, setMatchId] = useState<string | null>(null)
  const { caseId, identityId } = context
  const { query, items, retry } = audio
  const canEdit = query.data?.member_role !== 'viewer'
  return (
    <div className="space-y-6">
      <AudioHeader actions={{ refresh: retry, add: canEdit ? () => setOpen(true) : undefined }} />
      <AudioWarnings audio={audio} />
      <IdentityVoices
        caseId={caseId}
        identityId={identityId}
        pendingSamples={pendingSamples(items)}
      />
      <IdentityAudioList items={items} caseId={caseId} onCompare={setMatchId} />
      <RecordingMatchDialog
        context={matchId ? { caseId, subjectRef: identityId, matchId } : null}
        onClose={() => setMatchId(null)}
      />
      {canEdit && (
        <IdentityVoiceSample
          context={{ caseId, identityId }}
          dialog={{ open, onClose: () => setOpen(false) }}
          audio={audio}
        />
      )}
    </div>
  )
}

function AudioWarnings({ audio }: { audio: ReturnType<typeof useIdentityAudio> }) {
  const { t } = useTranslation()
  return (
    <>
      {audio.states.isError && (
        <p role="alert" className="text-sm text-destructive">
          {t(`${COPY}.checkDelayed`)}
        </p>
      )}
      {audio.items.some(row => row.voice_quality) && (
        <p className="text-xs text-muted-foreground">
          {t('humint.voice.sample.verdict.provisional')}
        </p>
      )}
    </>
  )
}

function IdentityVoiceSample({
  context,
  dialog,
  audio,
}: {
  context: { caseId: string; identityId: string }
  dialog: { open: boolean; onClose: () => void }
  audio: ReturnType<typeof useIdentityAudio>
}) {
  const { onStored, states, items } = audio
  return (
    <VoiceSampleHost
      {...dialog}
      mode={{
        kind: 'upload',
        caseId: context.caseId,
        subjectRef: context.identityId,
        onStored,
        captureStateOf: id =>
          states.data?.find(row => row.capture_id === id)?.state ??
          items.find(row => row.capture_id === id)?.state,
        captureQualityOf: id =>
          toVoiceSampleQuality(
            states.data?.find(row => row.capture_id === id)?.voice_quality ??
              items.find(row => row.capture_id === id)?.voice_quality
          ),
      }}
    />
  )
}
